import { useEffect } from "react";

export function useOfficeHoursCountdown() {
  useEffect(() => {
    const TZ = "America/Los_Angeles";
    const FIRST_Y = 2025;
    const FIRST_M = 10;
    const FIRST_D = 8;
    const HOUR = 10;
    const MIN = 30;
    const PERIOD_DAYS = 14;
    const MS_DAY = 86400000;

    const elDate = document.getElementById("oh-next-date");
    const elCountdown = document.getElementById("oh-countdown");
    if (!elDate || !elCountdown) return;

    function getTZParts(date: Date, timeZone: string) {
      const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      const parts = Object.fromEntries(
        dtf.formatToParts(date).map((p) => [p.type, p.value]),
      );
      return {
        y: +parts.year,
        m: +parts.month,
        d: +parts.day,
        H: +parts.hour,
        M: +parts.minute,
        S: +parts.second,
      };
    }

    function tzOffsetAt(instant: number, timeZone: string) {
      const p = getTZParts(new Date(instant), timeZone);
      const asUTC = Date.UTC(p.y, p.m - 1, p.d, p.H, p.M, p.S);
      return asUTC - instant;
    }

    function makeZonedDate(
      y: number,
      m: number,
      d: number,
      H: number,
      M: number,
      timeZone: string,
    ) {
      const base = Date.UTC(y, m - 1, d, H, M, 0);
      const off1 = tzOffsetAt(base, timeZone);
      const pass1 = base - off1;
      const off2 = tzOffsetAt(pass1, timeZone);
      return new Date(Date.UTC(y, m - 1, d, H, M, 0) - off2);
    }

    function addDaysLocal(y: number, m: number, d: number, days: number) {
      const t = Date.UTC(y, m - 1, d) + days * MS_DAY;
      const p = getTZParts(new Date(t), "UTC");
      return { y: p.y, m: p.m, d: p.d };
    }

    function computeNextSessionInstant() {
      const now = new Date();
      const startDayUTC = Date.UTC(FIRST_Y, FIRST_M - 1, FIRST_D);
      const nowPT = getTZParts(now, TZ);
      const nowDayUTC = Date.UTC(nowPT.y, nowPT.m - 1, nowPT.d);
      const daysSince = Math.floor((nowDayUTC - startDayUTC) / MS_DAY);
      const k = Math.max(0, Math.floor(daysSince / PERIOD_DAYS));

      let cand = addDaysLocal(FIRST_Y, FIRST_M, FIRST_D, k * PERIOD_DAYS);
      let candInstant = makeZonedDate(cand.y, cand.m, cand.d, HOUR, MIN, TZ);

      if (candInstant <= now) {
        cand = addDaysLocal(cand.y, cand.m, cand.d, PERIOD_DAYS);
        candInstant = makeZonedDate(cand.y, cand.m, cand.d, HOUR, MIN, TZ);
      }
      return candInstant;
    }

    function formatNextDatePT(date: Date) {
      const df = new Intl.DateTimeFormat(undefined, {
        timeZone: TZ,
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      const tf = new Intl.DateTimeFormat(undefined, {
        timeZone: TZ,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      const iso = new Date(date).toISOString();
      document.getElementById("oh-next-date")?.setAttribute("datetime", iso);
      return `${df.format(date)} · ${tf.format(date)} PT`;
    }

    let nextInstant = computeNextSessionInstant();
    if (elDate) {
      elDate.textContent = formatNextDatePT(nextInstant);
    }

    function tick() {
      const now = Date.now();
      const diff = nextInstant.getTime() - now;

      if (diff <= 0) {
        nextInstant = computeNextSessionInstant();
        if (elDate) {
          elDate.textContent = formatNextDatePT(nextInstant);
        }
        return;
      }
      const totalSec = Math.floor(diff / 1000);
      const days = Math.floor(totalSec / 86400);
      const hrs = Math.floor((totalSec % 86400) / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;

      const parts = [];
      if (days) parts.push(`${days}d`);
      parts.push(`${String(hrs).padStart(2, "0")}h`);
      parts.push(`${String(mins).padStart(2, "0")}m`);
      parts.push(`${String(secs).padStart(2, "0")}s`);

      if (elCountdown) {
        elCountdown.textContent = `Starts in ${parts.join(" ")}`;
      }
    }

    tick();
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, []);
}
